import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Target, Plus, MoreVertical, Trash2, CheckCircle2, Circle } from 'lucide-react';
import { toast } from 'sonner';

export default function MilestonesPage() {
  const [milestones, setMilestones] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadMilestones();
  }, []);

  const loadMilestones = async () => {
    try {
      const res = await api.get('/milestones');
      setMilestones(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const createMilestone = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const res = await api.post('/milestones', {
        title: title.trim(),
        description: description.trim() || null,
      });
      setMilestones(prev => [res.data, ...prev]);
      setTitle('');
      setDescription('');
      setDialogOpen(false);
      toast.success('Milestone created');
    } catch (err) {
      toast.error('Could not create milestone');
    } finally {
      setSaving(false);
    }
  };

  const toggleMilestone = async (milestone) => {
    try {
      const res = await api.put(`/milestones/${milestone.milestone_id}`, {
        completed: !milestone.completed,
      });
      setMilestones(prev => prev.map(m =>
        m.milestone_id === milestone.milestone_id ? res.data : m
      ));
      if (!milestone.completed) {
        toast.success('Milestone achieved! Beautiful work.');
      }
    } catch (err) {
      toast.error('Could not update');
    }
  };

  const deleteMilestone = async (milestoneId) => {
    try {
      await api.delete(`/milestones/${milestoneId}`);
      setMilestones(prev => prev.filter(m => m.milestone_id !== milestoneId));
      toast.success('Milestone removed');
    } catch (err) {
      toast.error('Could not delete');
    }
  };

  const completedCount = milestones.filter(m => m.completed).length;
  const progress = milestones.length > 0 ? Math.round((completedCount / milestones.length) * 100) : 0;
  const active = milestones.filter(m => !m.completed);
  const completed = milestones.filter(m => m.completed);

  return (
    <div className="space-y-8" data-testid="milestones-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 animate-fade-up">
        <div>
          <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-2">Your Evolution</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-dark tracking-tight mystical-heading">Milestones</h1>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-milestone-button"
              className="bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider btn-hover"
            >
              <Plus size={16} className="mr-2" /> New Milestone
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-bone border-black/10 rounded-2xl" data-testid="create-milestone-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-dark">Set a New Milestone</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What are you committing to?"
                data-testid="milestone-title-input"
                className="h-12 bg-sand/50 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans"
              />
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Why does this matter to your highest self? (optional)"
                data-testid="milestone-description-input"
                className="min-h-[100px] bg-sand/50 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans resize-none"
              />
              <Button
                onClick={createMilestone}
                disabled={!title.trim() || saving}
                data-testid="save-milestone-button"
                className="w-full bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider btn-hover"
              >
                {saving ? 'Creating...' : 'Create Milestone'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Progress Overview */}
      {milestones.length > 0 && (
        <div className="mystical-card p-8 animate-fade-up delay-100" data-testid="milestones-progress-overview">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-serif text-3xl text-dark">
                {completedCount} <span className="text-text-muted text-lg font-sans">of {milestones.length}</span>
              </p>
              <p className="text-text-muted font-sans text-sm mt-1">milestones achieved</p>
            </div>
            <div className="text-right">
              <p className="font-serif text-4xl text-gold">{progress}%</p>
            </div>
          </div>
          <Progress value={progress} className="h-2 bg-sand" />
        </div>
      )}

      {/* Active Milestones */}
      {active.length > 0 && (
        <div className="space-y-3">
          <p className="text-text-muted font-sans text-xs tracking-[0.2em] uppercase">In Progress</p>
          {active.map((milestone, i) => (
            <div
              key={milestone.milestone_id}
              className="glass-card p-5 lg:p-6 flex items-start gap-4 animate-fade-up"
              style={{ animationDelay: `${i * 0.05}s` }}
              data-testid={`milestone-${milestone.milestone_id}`}
            >
              <button
                onClick={() => toggleMilestone(milestone)}
                className="mt-0.5 text-text-muted hover:text-gold shrink-0"
                data-testid={`toggle-milestone-${milestone.milestone_id}`}
                style={{ transition: 'color 0.2s ease' }}
              >
                <Circle size={22} />
              </button>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg text-dark">{milestone.title}</p>
                {milestone.description && (
                  <p className="text-text-muted font-sans text-sm mt-1 leading-relaxed">{milestone.description}</p>
                )}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="text-text-muted hover:text-dark shrink-0 p-1" data-testid={`milestone-menu-${milestone.milestone_id}`}>
                    <MoreVertical size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-bone border-black/10 rounded-xl">
                  <DropdownMenuItem
                    onClick={() => deleteMilestone(milestone.milestone_id)}
                    className="text-red-500 focus:text-red-600 font-sans text-sm"
                    data-testid={`delete-milestone-${milestone.milestone_id}`}
                  >
                    <Trash2 size={14} className="mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      )}

      {/* Completed Milestones */}
      {completed.length > 0 && (
        <div className="space-y-3">
          <p className="text-text-muted font-sans text-xs tracking-[0.2em] uppercase">Achieved</p>
          {completed.map((milestone, i) => (
            <div
              key={milestone.milestone_id}
              className="glass-card p-5 lg:p-6 flex items-start gap-4 opacity-70 animate-fade-up"
              style={{ animationDelay: `${i * 0.05}s` }}
              data-testid={`milestone-${milestone.milestone_id}`}
            >
              <button
                onClick={() => toggleMilestone(milestone)}
                className="mt-0.5 text-gold shrink-0"
                data-testid={`toggle-milestone-${milestone.milestone_id}`}
              >
                <CheckCircle2 size={22} />
              </button>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg text-dark line-through decoration-gold/40">{milestone.title}</p>
                {milestone.completed_at && (
                  <p className="text-text-muted font-sans text-xs mt-1">
                    Achieved {new Date(milestone.completed_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="text-text-muted hover:text-dark shrink-0 p-1" data-testid={`milestone-menu-${milestone.milestone_id}`}>
                    <MoreVertical size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-bone border-black/10 rounded-xl">
                  <DropdownMenuItem
                    onClick={() => deleteMilestone(milestone.milestone_id)}
                    className="text-red-500 focus:text-red-600 font-sans text-sm"
                    data-testid={`delete-milestone-${milestone.milestone_id}`}
                  >
                    <Trash2 size={14} className="mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {milestones.length === 0 && (
        <div className="text-center py-16 animate-fade-up">
          <Target size={36} className="mx-auto text-text-muted/30 mb-4" />
          <p className="font-serif text-xl text-text-muted mb-2">No milestones yet</p>
          <p className="text-text-muted font-sans text-sm mb-6">
            Set meaningful milestones on your journey of becoming
          </p>
        </div>
      )}
    </div>
  );
}
